import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
export const KEY_PREFIX = 'eco_';
export function useSearchParams() {
  const history = useHistory();
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pushHistory = () => {
      const searchString = params.toString();
      const path = searchString ? `${location.pathname}?${searchString}` : location.pathname;
      history.push(path);
    };
    return {
      /**
       * Get a URL param, optionally restricting the allowed values.
       * If the value is not in `allowedValues`, `undefined` is returned.
       */
      get: (key, {
        allowedValues
      } = {}) => {
        if (!key) return undefined;
        const value = params.get(KEY_PREFIX + key);

        // return empty string if outside of allowed values
        if (allowedValues && !allowedValues.includes(value !== null && value !== void 0 ? value : '')) return undefined;

        // Ensure null from params.get() becomes undefined
        return value !== null && value !== void 0 ? value : undefined;
      },
      /**
       * Get all values for a URL parameter with the same key.
       * This is used for multi-value parameters where the same key appears multiple times.
       */
      getList: key => {
        const value = params.getAll(KEY_PREFIX + key);
        return value;
      },
      getAll: () => {
        const result = {};
        const uniqueKeys = new Set();
        for (const key of params.keys()) {
          if (!key.includes(KEY_PREFIX)) continue;
          uniqueKeys.add(key.replace(KEY_PREFIX, ''));
        }
        for (const key of uniqueKeys) {
          result[key] = params.getAll(KEY_PREFIX + key);
        }
        return result;
      },
      keys: () => Array.from(new Set(Array.from(params.keys()).filter(key => key.startsWith(KEY_PREFIX)))).map(key => key.replace(KEY_PREFIX, '')),
      /**
       * Set a URL param.
       * If `value` matches `defaultValue`, the param is removed instead.
       */
      set: (key, value, {
        defaultValue
      } = {}) => {
        if (!value || value === defaultValue) {
          params.delete(KEY_PREFIX + key);
        } else {
          params.set(KEY_PREFIX + key, value);
        }
        pushHistory();
      },
      setList: (key, values) => {
        params.delete(KEY_PREFIX + key);
        values.forEach(value => {
          params.append(KEY_PREFIX + key, value);
        });
        pushHistory();
      },
      delete: key => {
        params.delete(KEY_PREFIX + key);
        pushHistory();
      },
      /**
       * Deletes a value from a list of values for the given key.
       */
      deleteFromList: (key, valueToDelete) => {
        const prefixedKey = KEY_PREFIX + key;
        const currentValues = params.getAll(prefixedKey);
        const newValues = currentValues.filter(v => v !== valueToDelete);

        // Delete all existing instances of the key
        params.delete(prefixedKey);

        // Append the remaining values
        newValues.forEach(value => {
          params.append(prefixedKey, value);
        });
        pushHistory();
      }
    };
  }, [history, location]);
}